import React, { useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";

export default function PublicLogout() {
  const auth = useAuth();
  const hasLoggedOutRef = useRef(false); // Ref to track if logout has been initiated

  useEffect(() => {
    // Prevent re-execution if logout has already been initiated or auth/settings are not ready
    if (hasLoggedOutRef.current || !auth || !auth.settings || !auth.settings.metadata) {
      return;
    }

    const { client_id, post_logout_redirect_uri, metadata } = auth.settings;

    // Ensure necessary settings are available from the AuthProvider configuration
    if (!client_id || !post_logout_redirect_uri || !metadata?.end_session_endpoint) {
      console.error(
        "OIDC logout settings are not fully available from auth.settings. Cannot proceed with logout."
      );
      return;
    }

    // 1) Disable automatic silent‐renew so it can’t re‐authenticate via iframe.
    auth.settings.automaticSilentRenew = false;

    // 2) Grab the current ID token (if present) so Cognito knows which session to end.
    const idToken = auth.user?.id_token ?? "";

    // 3) Immediately remove the user from React–OIDC state. This might trigger a re-render.
    auth.removeUser();

    // 4) Build the Cognito Hosted UI logout URL.
    const encodedLogoutUri = encodeURIComponent(post_logout_redirect_uri);

    // Append &id_token_hint= only if we actually have an ID token.
    const idHintParam = idToken
      ? `&id_token_hint=${encodeURIComponent(idToken)}`
      : "";

    // auth.settings.metadata.end_session_endpoint should be the full base URL for logout
    // e.g., "https://<your-cognito-domain>/logout"
    const logoutUrl =
      `${metadata.end_session_endpoint}` +
      `?client_id=${client_id}` +
      `&logout_uri=${encodedLogoutUri}` +
      idHintParam;

    hasLoggedOutRef.current = true; // Mark that logout has been initiated
    // 5) Send the browser to Cognito’s `/logout` endpoint. Once Cognito clears its cookie,
    //    it will 302 back to your post_logout_redirect_uri.
    window.location.replace(logoutUrl);
  }, [auth]);

  // You can display a spinner or a simple message while the redirect is happening.
  return <div>Signing you out…</div>;
}
